import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Keyboard, X } from 'lucide-react';
import { useData } from '../context/DataContext';

interface ShortcutAction {
  key: string;
  description: string;
  action: () => void;
  modifiers?: string[];
}

interface KeyboardShortcutsProps {
  currentTab: string;
  onTabChange: (tab: string) => void;
  onViewModeChange?: (mode: 'micro' | 'macro' | 'files') => void;
  currentViewMode?: 'micro' | 'macro' | 'files';
}

const KeyboardShortcuts: React.FC<KeyboardShortcutsProps> = ({
  currentTab,
  onTabChange,
  onViewModeChange,
  currentViewMode
}) => {
  const [showHelp, setShowHelp] = useState(false);
  const {
    sections,
    selectedSectionIndex,
    setSelectedSectionIndex,
    selectedDocIds,
    documents,
    setSelectedDocIds
  } = useData();

  // Define shortcuts
  const shortcuts: ShortcutAction[] = [
    // Tab navigation
    {
      key: '1',
      description: 'Go to Dashboard',
      action: () => onTabChange('dashboard'),
      modifiers: ['Alt']
    },
    {
      key: '2',
      description: 'Go to Knowledge Graph',
      action: () => onTabChange('knowledge-graph'),
      modifiers: ['Alt']
    },
    {
      key: '3',
      description: 'Go to Chat',
      action: () => onTabChange('chat'),
      modifiers: ['Alt']
    },
    {
      key: '4',
      description: 'Go to Data Upload',
      action: () => onTabChange('data'),
      modifiers: ['Alt']
    },
    // Section navigation
    {
      key: 'ArrowLeft',
      description: 'Previous Section',
      action: () => {
        if (sections.length > 0) {
          setSelectedSectionIndex((selectedSectionIndex - 1 + sections.length) % sections.length);
        }
      },
      modifiers: ['Ctrl']
    },
    {
      key: 'ArrowRight',
      description: 'Next Section',
      action: () => {
        if (sections.length > 0) {
          setSelectedSectionIndex((selectedSectionIndex + 1) % sections.length);
        }
      },
      modifiers: ['Ctrl']
    },
    // View mode switching (for KG)
    {
      key: 'm',
      description: 'Switch to Micro KG',
      action: () => onViewModeChange?.('micro'),
      modifiers: ['Alt']
    },
    {
      key: 'M',
      description: 'Switch to Macro KG',
      action: () => onViewModeChange?.('macro'),
      modifiers: ['Alt', 'Shift']
    },
    {
      key: 'f',
      description: 'Switch to Files KG',
      action: () => {
        if (selectedDocIds.length > 0) {
          onViewModeChange?.('files');
        }
      },
      modifiers: ['Alt']
    },
    // File selection
    {
      key: 'a',
      description: 'Select All Files',
      action: () => {
        if (documents.length > 0) {
          setSelectedDocIds(documents.map(d => d.doc_id));
        }
      },
      modifiers: ['Ctrl', 'Shift']
    },
    {
      key: 'd',
      description: 'Deselect All Files',
      action: () => {
        setSelectedDocIds([]);
      },
      modifiers: ['Ctrl', 'Shift']
    },
    // Help
    {
      key: '?',
      description: 'Show/Hide Keyboard Shortcuts',
      action: () => setShowHelp(!showHelp),
      modifiers: ['Shift']
    }
  ];

  // Handle keyboard events
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Check each shortcut
    for (const shortcut of shortcuts) {
      const modifiersMatch =
        (!shortcut.modifiers || shortcut.modifiers.length === 0) ||
        (shortcut.modifiers?.includes('Ctrl') === event.ctrlKey &&
         shortcut.modifiers?.includes('Alt') === event.altKey &&
         shortcut.modifiers?.includes('Shift') === event.shiftKey);

      if (modifiersMatch && event.key === shortcut.key) {
        event.preventDefault();
        shortcut.action();
        return;
      }
    }
  }, [shortcuts]);

  // Register keyboard event listeners
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Helper to format modifier keys
  const formatModifiers = (modifiers?: string[]) => {
    if (!modifiers || modifiers.length === 0) return '';
    return modifiers.map(m => {
      switch (m) {
        case 'Ctrl': return '⌃';
        case 'Alt': return '⌥';
        case 'Shift': return '⇧';
        default: return m;
      }
    }).join(' ') + ' ';
  };

  // Helper to format key
  const formatKey = (key: string) => {
    switch (key) {
      case 'ArrowLeft': return '←';
      case 'ArrowRight': return '→';
      case 'ArrowUp': return '↑';
      case 'ArrowDown': return '↓';
      default: return key;
    }
  };

  return (
    <>
      {/* Floating Help Button */}
      <motion.button
        onClick={() => setShowHelp(!showHelp)}
        className="fixed bottom-6 right-6 z-40 p-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-full shadow-2xl transition-all"
        whileHover={{ scale: 1.1, rotate: 5 }}
        whileTap={{ scale: 0.95 }}
        title="Keyboard Shortcuts (Shift + ?)"
      >
        <Keyboard size={24} />
      </motion.button>

      {/* Help Modal */}
      <AnimatePresence>
        {showHelp && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowHelp(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900 rounded-2xl p-8 max-w-3xl w-full max-h-[80vh] overflow-y-auto border border-purple-500/30 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg">
                    <Keyboard size={24} className="text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-white">Keyboard Shortcuts</h2>
                </div>
                <button
                  onClick={() => setShowHelp(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors text-gray-400 hover:text-white"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Shortcuts List */}
              <div className="space-y-6">
                {/* Navigation Shortcuts */}
                <div>
                  <h3 className="text-lg font-semibold text-purple-300 mb-3">Navigation</h3>
                  <div className="space-y-2">
                    {shortcuts.filter(s =>
                      ['1', '2', '3', '4', 'ArrowLeft', 'ArrowRight'].includes(s.key)
                    ).map((shortcut, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                      >
                        <span className="text-gray-300">{shortcut.description}</span>
                        <kbd className="px-3 py-1.5 bg-gray-800 text-white rounded-lg border border-gray-700 font-mono text-sm">
                          {formatModifiers(shortcut.modifiers)}{formatKey(shortcut.key)}
                        </kbd>
                      </div>
                    ))}
                  </div>
                </div>

                {/* View Mode Shortcuts */}
                <div>
                  <h3 className="text-lg font-semibold text-blue-300 mb-3">Knowledge Graph Views</h3>
                  <div className="space-y-2">
                    {shortcuts.filter(s =>
                      ['m', 'M', 'f'].includes(s.key)
                    ).map((shortcut, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                      >
                        <span className="text-gray-300">{shortcut.description}</span>
                        <kbd className="px-3 py-1.5 bg-gray-800 text-white rounded-lg border border-gray-700 font-mono text-sm">
                          {formatModifiers(shortcut.modifiers)}{formatKey(shortcut.key)}
                        </kbd>
                      </div>
                    ))}
                  </div>
                </div>

                {/* File Selection Shortcuts */}
                <div>
                  <h3 className="text-lg font-semibold text-green-300 mb-3">File Selection</h3>
                  <div className="space-y-2">
                    {shortcuts.filter(s =>
                      ['a', 'd'].includes(s.key)
                    ).map((shortcut, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                      >
                        <span className="text-gray-300">{shortcut.description}</span>
                        <kbd className="px-3 py-1.5 bg-gray-800 text-white rounded-lg border border-gray-700 font-mono text-sm">
                          {formatModifiers(shortcut.modifiers)}{formatKey(shortcut.key)}
                        </kbd>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Other Shortcuts */}
                <div>
                  <h3 className="text-lg font-semibold text-yellow-300 mb-3">Other</h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors">
                      <span className="text-gray-300">Toggle Fullscreen (KG)</span>
                      <kbd className="px-3 py-1.5 bg-gray-800 text-white rounded-lg border border-gray-700 font-mono text-sm">
                        F11
                      </kbd>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors">
                      <span className="text-gray-300">Exit Fullscreen / Clear Highlight</span>
                      <kbd className="px-3 py-1.5 bg-gray-800 text-white rounded-lg border border-gray-700 font-mono text-sm">
                        Esc
                      </kbd>
                    </div>
                    {shortcuts.filter(s => s.key === '?').map((shortcut, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                      >
                        <span className="text-gray-300">{shortcut.description}</span>
                        <kbd className="px-3 py-1.5 bg-gray-800 text-white rounded-lg border border-gray-700 font-mono text-sm">
                          {formatModifiers(shortcut.modifiers)}{formatKey(shortcut.key)}
                        </kbd>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Current Context */}
              <div className="mt-6 p-4 bg-white/5 rounded-lg border border-purple-500/30">
                <h3 className="text-sm font-semibold text-purple-300 mb-2">Current Context</h3>
                <div className="space-y-1 text-sm text-gray-400">
                  <div className="flex justify-between">
                    <span>Active Tab:</span>
                    <span className="text-white font-medium">{currentTab}</span>
                  </div>
                  {currentViewMode && (
                    <div className="flex justify-between">
                      <span>View Mode:</span>
                      <span className="text-white font-medium capitalize">{currentViewMode}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>Selected Files:</span>
                    <span className="text-white font-medium">{selectedDocIds.length} / {documents.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Current Section:</span>
                    <span className="text-white font-medium">
                      {sections.length > 0 ? `${selectedSectionIndex + 1} / ${sections.length}` : 'None'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-6 text-center text-sm text-gray-400">
                <p>Press <kbd className="px-2 py-1 bg-gray-800 text-white rounded border border-gray-700 font-mono text-xs">Shift + ?</kbd> to toggle this help</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default KeyboardShortcuts;
